import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild
} from '@angular/core';
import { AuthService } from '../../../services/auth.service';
import { Subscription } from 'rxjs';
import { User } from '../../../models/User';
import { SchedulerEvent } from '../../../models/SchedulerEvent';
import { Customer } from '../../../models/Customer';
import { CustomerService } from '../../../services/customer.service';
import { Button } from '../../../models/Button';
import { Router, RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SettingService } from '../../../services/setting.service';
import { Setting } from '../../../models/Setting';
import { DialogConfirmComponent } from '../../shared/dialog-confirm/dialog-confirm.component';
import { Splitter, SplitterResizeEndEvent } from 'primeng/splitter';
import { EventsComponent } from './events/events.component';
import { NgTemplateOutlet } from '@angular/common';
import { Drawer } from 'primeng/drawer';
import { DatePicker } from 'primeng/datepicker';
import { AddButtonComponent } from '../../shared/add-button/add-button.component';
import { CustomerComponent } from './customer/customer.component';
import { EventComponent } from './event/event.component';
import { RecentCustomersComponent } from './recent-customers/recent-customers.component';
import { PrimeTemplate } from 'primeng/api';
import { ButtonDirective } from 'primeng/button';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Aline dashboard which shows the user specific information like
 * Selected day schedule, to do list, etc.
 * Dashboard can contains different items based on the user role.
 */
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],

  imports: [
    DialogConfirmComponent,
    Splitter,
    EventsComponent,
    NgTemplateOutlet,
    Drawer,
    RouterLink,
    DatePicker,
    AddButtonComponent,
    CustomerComponent,
    EventComponent,
    RecentCustomersComponent,
    PrimeTemplate,
    ButtonDirective,
    TranslatePipe
  ]
})
export class HomeComponent implements OnInit, AfterViewInit {
  private userProfileSubscription$: Subscription | null = null;
  private modules: Array<any>;
  private rightSidebarActive = false;

  public rightNavi: string;
  public activeModules: Array<any>;
  public user: User | null = null;
  public selectedDate: Date;
  public customer: Customer | null = null;
  public selectedEvent: SchedulerEvent | null = null;
  public startEventBtn: Button;
  public roomSettings: Array<Setting>;
  public displayRightSidebar = false;
  public mainPanelSizes: number[] = [75, 25];

  @ViewChild('sidebarBtn') sidebarButtonRef: ElementRef | undefined;

  // TODO: pitäisi olla joku yleinen toteutus, jonka avulla saisi selville ollaanko minkälaisessa laitteessa
  private isSidebarActive(): void {
    this.rightSidebarActive = false;
    if (this.sidebarButtonRef?.nativeElement.offsetTop > 0) {
      this.rightSidebarActive = true;
    }
  }

  constructor(
    private router: Router,
    private authService: AuthService,
    private customerService: CustomerService,
    private translateService: TranslateService,
    private settingService: SettingService
  ) {
    this.selectedDate = new Date();
    this.activeModules = [];
    this.modules = [];
    this.roomSettings = new Array<Setting>();
    this.rightNavi = 'calendar';
    this.startEventBtn = new Button();
    this.startEventBtn.id = 'startEvent';
    this.translateService
      .get('common.button.start-appointment')
      .subscribe((result) => {
        this.startEventBtn.title = result;
      });
  }

  async ngOnInit(): Promise<void> {
    this.userProfileSubscription$ =
      this.authService.userProfileObservable$.subscribe((profile) => {
        this.user = profile;
      });
    // Dashboard is not tied to a specific customer/patient, so the top bar
    // should not keep showing whatever was previously selected.
    await this.customerService.setSelectedCustomer(null);
    this.roomSettings = await this.settingService.getSettingByName('ROOM');

    this.settingService.getUserSettings().subscribe((cfg) => {
      if (Array.isArray(cfg?.splitters?.main)) {
        this.mainPanelSizes = cfg.splitters.main;
      }
    });
  }

  /**
   * Persists the main content/calendar splitter position for the current user.
   */
  onMainSplitterResizeEnd(event: SplitterResizeEndEvent): void {
    this.settingService
      .saveSplitterSizes('main', event.sizes.map(Number))
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.isSidebarActive();
  }

  @HostListener('window:resize')
  checkObjectsVisibility(): void {
    this.isSidebarActive();
  }

  /**
   * Sets the selected date from the calendar view.
   * In mobile view the calendar is opened to sidebar and the date selection should
   * also close the sidebar.
   *
   * @param date: Selected date
   */
  setDate(date: any): void {
    this.selectedDate = date;
    if (this.rightSidebarActive) {
      this.displayRightSidebar = false;
    }
  }

  /**
   * Set the selected event and fetch the event specific information.
   * In mobile view the event information is shown in sidebar so it have to open.
   *
   * @param event: Selected scheduler event. Can be appointment, to do event, etc.
   */
  async selectEvent(event: SchedulerEvent): Promise<void> {
    // TODO: EventType ratkaisee sen mitä seuraavaksi tehdään. Samoin se, onko tapahtumaan liitetty asiakasta
    this.selectedEvent = event;
    this.customer = null;

    if (this.selectedEvent.eventType === 'appointment') {
      if (this.selectedEvent.customerId) {
        const customerInfo = await this.customerService.getCustomer(
          this.selectedEvent.customerId
        );
        const partner = await this.customerService.getPartner(customerInfo);
        if (partner) {
          customerInfo.partner = partner;
        }
        this.customer = customerInfo;
      }
    }
    this.rightNavi = 'event';
    if (this.rightSidebarActive) {
      this.displayRightSidebar = true;
    }
  }

  async startEvent(): Promise<void> {
    await this.customerService.setSelectedCustomer(this.customer);

    if (
      this.customer!.customerType === 'd' ||
      this.customer!.customerType === 'e'
    ) {
      await this.router.navigate(['donor/healthrecord/list']);
    } else {
      await this.router.navigate(['customer/healthrecord/list']);
    }
    /*
    await this.router.navigate([
      'customer/healthrecord/list',
      this.customer!.id,
    ]);
    */
  }
}
